import axiosConfig from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createListFetcher } from './listFetchers';
import { queryClient } from './queryClient';

// List rides
export const getRideList = createListFetcher('ride');

// Create ride
export const createRide = async (rideData) => {
    try {
        const response = await axiosConfig.post('ride', rideData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
        return response;
    } catch (error) {
        throw error || 'Failed to create ride';
    }
};

// Show ride details
export const getRideDetails = async (id) => {
    try {
        const response = await axiosConfig.get(`ride/${id}`);
        return response;
    } catch (error) {
        throw error;
    }
};

// Update ride
export const updateRide = async (id, rideData) => {
    try {
        const response = await axiosConfig.put(`ride/${id}`, rideData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });
        return response;
    } catch (error) {
        throw error || 'Failed to update ride';
    }
};

// Delete ride
export const deleteRide = async (id) => {
    try {
        const response = await axiosConfig.delete(`ride/${id}`);
        return response;
    } catch (error) {
        throw error || 'Failed to delete ride';
    }
};

// React Query hooks
export const useRideDetails = (id, options = {}) => useQuery({
    queryKey: ['rideDetails', id],
    queryFn: () => getRideDetails(id).then(response => response.data),
    enabled: !!id,
    ...options,
});

export const useRideList = (options = {}) => useQuery({
    queryKey: ['rides', 'all'],
    queryFn: () => axiosConfig.get('ride', { params: { get_all: true } }).then(response => response.data?.data ?? []),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    ...options,
});

export const useCreateRide = (options = {}) => {
    return useMutation({
        mutationFn: createRide,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rides'] }),
        ...options,
    });
};

export const useUpdateRide = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, rideData }) => updateRide(id, rideData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['rides'] });
            if (variables?.id) {
                queryClient.invalidateQueries({ queryKey: ['rideDetails', variables.id] });
            }
        },
        ...options,
    });
};

export const useDeleteRide = (options = {}) => {
    return useMutation({
        mutationFn: deleteRide,
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['rides'] });
            queryClient.invalidateQueries({ queryKey: ['rideDetails', id] });
        },
        ...options,
    });
};
